import { useEffect, useMemo, useState, useCallback, useRef, useContext } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaChevronDown, FaPen, FaRedo } from "react-icons/fa";
import HomeRightSidebar from "../components/HomeRightSidebar";
import PoemCard from "../components/PoemCard";
import Skeleton, { SkeletonText } from "../components/Skeleton";
import EmptyState from "../components/EmptyState";
import { UserContext } from "../context/UserContext";
import {
  getAvatarUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import { useFeedSidebarAlign } from "../hooks/useFeedSidebarAlign";
import "./Home.css";

function avatarOnError(e) {
  e.currentTarget.src =
    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
      ? FALLBACK_AVATAR_SVG
      : DEFAULT_AVATAR_DATA_URL;
}

export default function Home() {
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = (searchParams.get("q") || "").trim();
  const sortFromUrl = searchParams.get("sort") === "likes" ? "likes" : "latest";
  const [activeTab, setActiveTab] = useState("all");
  const [sortMode, setSortMode] = useState(sortFromUrl);
  const [poems, setPoems] = useState([]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [adminPosts, setAdminPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [error, setError] = useState("");
  const observerRef = useRef();
  const expectedPoemsCountRef = useRef(0);
  const layoutRef = useFeedSidebarAlign([
    loading,
    activeTab,
    searchQuery,
    sortMode,
    adminPosts.length,
  ]);
  const isAppendingRef = useRef(false);
  const loadingStartTimeRef = useRef(0);

  useEffect(() => {
    setSortMode(sortFromUrl);
  }, [sortFromUrl]);

  const loadPoems = useCallback(async (
    page = 1,
    append = false,
    feedType = "all",
    query = "",
    sort = "latest"
  ) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
        isAppendingRef.current = true;
        loadingStartTimeRef.current = Date.now();
      }

      setError("");

      const poemSearch =
        feedType === "all" && query.trim() ? query.trim() : "";
      let endpoint =
        feedType === "following"
          ? getApiUrl(`/api/poems/following?page=${page}&limit=10`)
          : getApiUrl(`/api/poems?page=${page}&limit=10`);
      if (poemSearch) {
        endpoint += `&search=${encodeURIComponent(poemSearch)}`;
      }
      endpoint += `&sort=${sort === "likes" ? "likes" : "latest"}`;

      const res = await fetch(endpoint, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const poemsArray = data.poems || [];

      if (append) {
        setPoems((prev) => {
          expectedPoemsCountRef.current = prev.length + poemsArray.length;
          return [...prev, ...poemsArray];
        });
      } else {
        setPoems(poemsArray);
        setLoading(false);
        setLoadingMore(false);
        isAppendingRef.current = false;
      }

      setPagination(
        data.pagination || {
          currentPage: page,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        }
      );
    } catch (err) {
      console.error("Failed to fetch poems:", err);
      setError("Failed to load poems. Please try again.");
      if (!append) setPoems([]);
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadAdminPosts = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl("/api/admin/posts"));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAdminPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch admin posts:", err);
      setAdminPosts([]);
    }
  }, []);

  useEffect(() => {
    loadPoems(1, false, activeTab, searchQuery, sortMode);
    if (activeTab === "all" && !searchQuery && sortMode === "latest") {
      loadAdminPosts();
    } else {
      setAdminPosts([]);
    }
  }, [loadPoems, loadAdminPosts, activeTab, searchQuery, sortMode]);

  useEffect(() => {
    const handleBlockedChange = () => {
      setPoems([]);
      setPagination((p) => ({ ...p, currentPage: 1 }));
      loadPoems(1, false, activeTab, searchQuery, sortMode);
    };

    window.addEventListener("userBlocked", handleBlockedChange);
    window.addEventListener("userUnblocked", handleBlockedChange);
    return () => {
      window.removeEventListener("userBlocked", handleBlockedChange);
      window.removeEventListener("userUnblocked", handleBlockedChange);
    };
  }, [loadPoems, activeTab, searchQuery, sortMode]);

  const loadMorePoems = useCallback(() => {
    if (!loadingMore && pagination.hasNextPage) {
      loadPoems(pagination.currentPage + 1, true, activeTab, searchQuery, sortMode);
    }
  }, [
    loadingMore,
    pagination.hasNextPage,
    pagination.currentPage,
    loadPoems,
    activeTab,
    searchQuery,
    sortMode,
  ]);

  const handleSortModeChange = (next) => {
    if (next === sortMode) return;
    setSortMode(next);
    setPoems([]);
    setPagination((p) => ({ ...p, currentPage: 1 }));
    const params = new URLSearchParams(searchParams);
    if (next === "likes") {
      params.set("sort", "likes");
    } else {
      params.delete("sort");
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pagination.hasNextPage && !loadingMore) {
          loadMorePoems();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [loadMorePoems, pagination.hasNextPage, loadingMore]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const feedItems = useMemo(() => {
    const poemItems = poems.map((p) => ({
      type: "poem",
      data: p,
      createdAt: p.createdAt,
    }));

    if (sortMode !== "latest" || activeTab !== "all" || searchQuery) {
      return poemItems;
    }

    const adminItems = adminPosts.map((a) => ({
      type: "admin",
      data: a,
      createdAt: a.createdAt,
    }));
    return [...poemItems, ...adminItems].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [poems, adminPosts, sortMode, activeTab, searchQuery]);

  useEffect(() => {
    if (!isAppendingRef.current || expectedPoemsCountRef.current === 0) {
      return;
    }

    const poemCount = feedItems.filter((item) => item.type === "poem").length;

    if (
      poems.length >= expectedPoemsCountRef.current &&
      poemCount >= expectedPoemsCountRef.current
    ) {
      const elapsed = Date.now() - loadingStartTimeRef.current;
      const minDisplayTime = 1000;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              setTimeout(() => {
                setLoadingMore(false);
                isAppendingRef.current = false;
                expectedPoemsCountRef.current = 0;
                loadingStartTimeRef.current = 0;
              }, remainingTime + 500);
            }, 100);
          });
        });
      });
    }
  }, [feedItems, poems.length]);

  return (
    <div ref={layoutRef} className="home-page home-page--panes min-h-screen">
      <Helmet>
        <title>
          {searchQuery
            ? `Poems: ${searchQuery} – PeerPoetry`
            : sortMode === "likes"
            ? "Most Loved Poems – PeerPoetry"
            : "Latest Poems – PeerPoetry"}
        </title>
        <meta
          name="description"
          content={
            searchQuery
              ? `Poems matching "${searchQuery}" on PeerPoetry.`
              : "Explore the latest poems from the community. Discover trending tags and new voices on PeerPoetry."
          }
        />
        <link
          rel="canonical"
          href={
            searchQuery
              ? `${window.location.origin}/?q=${encodeURIComponent(searchQuery)}`
              : `${window.location.origin}/`
          }
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={
            searchQuery
              ? `Poems: ${searchQuery} – PeerPoetry`
              : sortMode === "likes"
              ? "Most Loved Poems – PeerPoetry"
              : "Latest Poems – PeerPoetry"
          }
        />
        <meta
          property="og:description"
          content="Explore the latest poems from the community."
        />
        <meta property="og:url" content={`${window.location.origin}/`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            searchQuery
              ? `Poems: ${searchQuery} – PeerPoetry`
              : sortMode === "likes"
              ? "Most Loved Poems – PeerPoetry"
              : "Latest Poems – PeerPoetry"
          }
        />
        <meta
          name="twitter:description"
          content="Explore the latest poems from the community."
        />
      </Helmet>
      <div className="home-page__layout">
      <div className="home-page__feed-body">
      <div className="home-page__feed-column">
      <div className="home-page__feed-head w-full">
        <h1 className="home-page__title">
          {searchQuery ? (
            <>Poems matching &ldquo;{searchQuery}&rdquo;</>
          ) : sortMode === "likes" ? (
            "Most Loved Poems"
          ) : (
            "Latest Poems"
          )}
        </h1>
        {!searchQuery && (
          <p className="home-page__subtitle">
            {sortMode === "likes"
              ? "Community favorites, ranked by love."
              : "Fresh verses from poets across PeerPoetry."}
          </p>
        )}
        {searchQuery && (
          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.delete("q");
              setSearchParams(params);
            }}
            className="home-page__clear-search"
          >
            Clear search and show all poems
          </button>
        )}

        <div className="home-page__toolbar">
          <div className="home-page__tabs" role="tablist" aria-label="Feed filter">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "all"}
              onClick={() => {
                setActiveTab("all");
                setPoems([]);
                setPagination((p) => ({ ...p, currentPage: 1 }));
              }}
              className={`home-page__tab${
                activeTab === "all" ? " home-page__tab--active" : ""
              }`}
            >
              All
            </button>
            {user && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "following"}
                onClick={() => {
                  setActiveTab("following");
                  setPoems([]);
                  setPagination((p) => ({ ...p, currentPage: 1 }));
                }}
                className={`home-page__tab${
                  activeTab === "following" ? " home-page__tab--active" : ""
                }`}
              >
                Following
              </button>
            )}
          </div>
          <label className="home-page__sort">
            <span className="home-page__sort-value" aria-hidden="true">
              {sortMode === "likes" ? "Most Liked" : "Latest"}
            </span>
            <FaChevronDown className="home-page__sort-icon" aria-hidden="true" />
            <select
              className="home-page__sort-select"
              value={sortMode}
              onChange={(e) => handleSortModeChange(e.target.value)}
              aria-label="Sort poems"
            >
              <option value="latest">Latest</option>
              <option value="likes">Most Liked</option>
            </select>
          </label>
        </div>
      </div>
      <div className="home-page__composer">
        {user && (
          <img
            src={getAvatarUrl(user.avatar)}
            alt=""
            className="home-page__composer-avatar"
            onError={avatarOnError}
          />
        )}
        <Link
          to={user ? "/write" : "/login"}
          state={user ? undefined : { from: "/write" }}
          className="home-page__composer-input"
        >
          {user
            ? "Share a poem with the community…"
            : "Sign in to share your poetry…"}
        </Link>
        <div className="home-page__composer-actions">
          <Link
            to={user ? "/write" : "/login"}
            state={user ? undefined : { from: "/write" }}
            className="home-page__composer-action"
          >
            <FaPen aria-hidden="true" />
            Write
          </Link>
        </div>
      </div>
      <div className="home-page__feed-list w-full">
        {loading ? (
          <>
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className="peer-home-card p-4 sm:p-6"
              >
                <Skeleton className="h-6 w-2/3 mb-3 bg-[#eeeeee]" />
                <SkeletonText lines={3} className="mb-3" />
                <div className="flex items-center gap-4 mt-4">
                  <Skeleton className="h-4 w-16 bg-[#eeeeee]" />
                  <Skeleton className="h-4 w-24 bg-[#eeeeee]" />
                </div>
              </div>
            ))}
          </>
        ) : error ? (
          <div className="text-center peer-home-card p-6 sm:p-8">
            <p className="text-red-700 mb-4 text-sm sm:text-base font-medium">
              {error}
            </p>
            <button
              type="button"
              onClick={() => loadPoems(1, false, activeTab, searchQuery)}
              className="peer-home-btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : feedItems.length === 0 && !loadingMore ? (
          <EmptyState
            icon="poem"
            title={
              searchQuery
                ? `No poems found for "${searchQuery}"`
                : activeTab === "following"
                ? "No Poems from Followed Users"
                : "No Poems Yet"
            }
            message={
              searchQuery
                ? "Try a different keyword or clear the search to browse all poems."
                : activeTab === "following"
                ? user
                  ? "You're not following anyone yet, or the users you follow haven't posted any poems. Start following poets to see their work here!"
                  : "Please log in to see poems from users you follow."
                : user
                ? "Be the first to share your poetry with the community! Your words can inspire others."
                : "There are no poems to display yet. Join our community to read and share beautiful poetry."
            }
            action={
              searchQuery || activeTab === "following" ? undefined : user ? "write" : undefined
            }
            actionLabel={
              searchQuery || activeTab === "following" ? undefined : user ? "Write Your First Poem" : undefined
            }
            actionLink={
              searchQuery || activeTab === "following" ? undefined : user ? "/write" : undefined
            }
          />
        ) : (
          <>
            {feedItems.map((item) =>
              item.type === "poem" ? (
                <div key={`poem:${item.data._id}`}>
                  <PoemCard poem={item.data} showSave={false} />
                </div>
              ) : (
                <article
                  key={`admin:${item.data._id}`}
                  className="home-announce"
                >
                  <p className="home-announce__eyebrow">From PeerPoetry</p>
                  {item.data.title ? (
                    <h3 className="home-announce__title">{item.data.title}</h3>
                  ) : null}
                  <p className="home-announce__body">{item.data.content}</p>
                  {item.data.image ? (
                    <img
                      src={getApiUrl(`${item.data.image}`)}
                      alt=""
                      className="home-announce__image"
                    />
                  ) : null}
                  <div className="home-announce__meta">
                    {new Date(item.data.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </article>
              )
            )}

            {loadingMore && (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#eeeeee] border-t-[#f26522]" />
                  <p className="text-[#777777] font-medium text-base sm:text-lg">
                    Loading more poems...
                  </p>
                </div>
              </div>
            )}

            {pagination.hasNextPage && !loadingMore && (
              <div ref={observerRef} className="text-center py-4">
                <button
                  type="button"
                  onClick={loadMorePoems}
                  className="home-page__load-more"
                >
                  <FaRedo aria-hidden="true" />
                  Load more
                </button>
              </div>
            )}

            {!pagination.hasNextPage && poems.length > 0 && (
              <div className="home-page__feed-end">
                <p className="home-page__feed-end-title">You&apos;ve reached the end</p>
                <p className="home-page__feed-end-sub">No more poems to load right now</p>
              </div>
            )}
          </>
        )}
      </div>
      </div>
        <aside className="home-page__rail">
          <HomeRightSidebar />
        </aside>
      </div>
      </div>
      {showBackToTop && (
        <button
          type="button"
          onClick={handleBackToTop}
          className="home-back-to-top peer-home-btn-primary rounded-full px-4 py-3 sm:px-5 shadow-lg z-50 text-sm sm:text-base"
          aria-label="Back to Top"
        >
          ↑ Back to Top
        </button>
      )}
    </div>
  );
}
